import React, { useState, useEffect } from 'react';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { config } from '../../config';

interface PaymentFormProps {
  enrollmentId: string;
  invoiceData: {
    id: string;
    invoice_number: string;
    total: number;
    due_date: string;
  };
  clientSecret: string;
  stripeMode: string;
  onSuccess: () => void;
  onCancel: () => void;
}

const PaymentForm: React.FC<PaymentFormProps> = ({
  enrollmentId,
  invoiceData,
  clientSecret,
  stripeMode,
  onSuccess,
  onCancel
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [postalCode, setPostalCode] = useState<string>('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);
    setError(null);

    try {
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        setError('Card element not found');
        setProcessing(false);
        return;
      }

      // Validate postal code
      const postalCodeRegex = /^[A-Z]\d[A-Z] \d[A-Z]\d$/;
      if (!postalCodeRegex.test(postalCode)) {
        setError('Please enter a valid Canadian postal code (e.g., K1A 0A6)');
        setProcessing(false);
        return;
      }

      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: {
            card: cardElement,
            billing_details: {
              address: {
                country: 'CA',
                postal_code: postalCode
              }
            }
          }
        }
      );

      if (stripeError) {
        setError(stripeError.message || 'Payment failed');
        setProcessing(false);
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        // Payment succeeded in Stripe, now record it in our backend
        try {
          const token = localStorage.getItem('cognitoToken') || '';
          const response = await fetch(
            `${config.BACKEND_URL}/api/camps/enrollments/${enrollmentId}/confirm-payment`,
            {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                payment_intent_id: paymentIntent.id
              })
            }
          );

          if (response.ok) {
            console.log('✅ Payment recorded in backend');
            onSuccess();
          } else {
            const errorData = await response.json();
            setError(`Payment succeeded but failed to record: ${errorData.detail || 'Unknown error'}`);
          }
        } catch (backendError: any) {
          console.error('Backend payment recording error:', backendError);
          setError('Payment succeeded but failed to record. Please contact support.');
        } finally {
          setProcessing(false);
        }
      } else {
        setProcessing(false);
      }
    } catch (err: any) {
      setError(err.message || 'Payment failed');
      setProcessing(false);
    }
  };

  return (
    <div style={{ padding: '30px' }}>
      <h3 style={{ marginBottom: '20px', fontSize: '22px' }}>Complete Payment</h3>
      
      <div style={{ marginBottom: '25px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', fontSize: '14px' }}>
          <div>
            <strong>Invoice:</strong> {invoiceData.invoice_number}
          </div>
          <div>
            <strong>Due Date:</strong> {new Date(invoiceData.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </div>
        </div>
        <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #dee2e6' }}>
          <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#667eea' }}>
            Amount Due: ${invoiceData.total.toFixed(2)}
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold', fontSize: '14px' }}>
            Card Details
          </label>
          <div style={{ 
            padding: '15px', 
            border: '1px solid #ddd', 
            borderRadius: '8px',
            backgroundColor: '#fff'
          }}>
            <CardElement options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#424770',
                  '::placeholder': { color: '#aab7c4' }
                },
                invalid: { color: '#9e2146' }
              },
              hidePostalCode: true
            }} />
          </div>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold', fontSize: '14px' }}>
            Postal Code (Canadian Format) *
          </label>
          <input
            type="text"
            value={postalCode}
            onChange={(e) => {
              const value = e.target.value.toUpperCase().replace(/[^A-Z0-9 ]/g, '');
              setPostalCode(value);
            }}
            placeholder="K1A 0A6"
            pattern="[A-Z][0-9][A-Z] [0-9][A-Z][0-9]"
            required
            style={{
              width: '100%',
              padding: '15px',
              border: '1px solid #ddd',
              borderRadius: '8px',
              fontSize: '16px',
              textTransform: 'uppercase'
            }}
          />
          <small style={{ fontSize: '12px', color: '#666', marginTop: '5px', display: 'block' }}>
            Format: A1A 1A1 (e.g., K1A 0A6)
          </small>
        </div>

        {error && (
          <div style={{ 
            padding: '15px', 
            backgroundColor: '#f8d7da', 
            color: '#721c24', 
            borderRadius: '8px', 
            marginBottom: '20px',
            fontSize: '14px'
          }}>
            ❌ {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '30px' }}>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={!stripe || processing}
            style={{ padding: '12px 30px', fontSize: '14px', fontWeight: 'bold' }}
          >
            {processing ? 'Processing...' : `Pay $${invoiceData.total.toFixed(2)}`}
          </button>
        </div>
      </form>

      <div style={{ 
        marginTop: '25px', 
        paddingTop: '20px', 
        borderTop: '1px solid #dee2e6',
        textAlign: 'center',
        fontSize: '12px',
        color: '#666'
      }}>
        <div style={{ marginBottom: '10px' }}>
          <span style={{ marginRight: '15px' }}>🔒 SSL Secured</span>
          <span style={{ marginRight: '15px' }}>🛡️ PCI Compliant</span>
          <span>✅ Stripe Protected</span>
        </div>
        <p style={{ margin: 0 }}>
          Your payment information is processed securely by Stripe. We never store your card details.
        </p>
      </div>
    </div>
  );
};

interface CampPaymentModalProps {
  enrollmentId: string;
  invoiceData: {
    id: string;
    invoice_number: string;
    total: number;
    due_date: string;
  };
  campName?: string;
  studentName?: string;
  onSuccess: () => void;
  onCancel: () => void;
}

const CampPaymentModal: React.FC<CampPaymentModalProps> = (props) => {
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [stripeMode, setStripeMode] = useState<string>('test');
  const [clientSecret, setClientSecret] = useState<string>('');
  const [initializing, setInitializing] = useState<boolean>(true);
  const [paymentInitError, setPaymentInitError] = useState<string | null>(null);
  const [paymentStep, setPaymentStep] = useState<'choice' | 'interac' | 'stripe'>('choice');
  const [sendingInteracEmail, setSendingInteracEmail] = useState<boolean>(false);

  useEffect(() => {
    if (paymentStep !== 'stripe') {
      return;
    }

    // Create payment intent once and initialize Stripe with the matching key.
    const initializeStripe = async () => {
      try {
        setInitializing(true);
        setPaymentInitError(null);
        const token = localStorage.getItem('cognitoToken') || '';
        const response = await fetch(
          `${config.BACKEND_URL}/api/camps/enrollments/${props.enrollmentId}/create-payment-intent`,
          {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );

        if (response.ok) {
          const data = await response.json();
          const publishableKey = data.publishable_key || config.STRIPE_PUBLISHABLE_KEY;
          const mode = data.stripe_mode || 'test';
          const secret = data.client_secret || '';
          
          console.log(`💳 Initializing Stripe with ${mode.toUpperCase()} mode key:`, publishableKey.substring(0, 20) + '...');
          setClientSecret(secret);
          setStripeMode(mode);
          setStripePromise(loadStripe(publishableKey));
        } else {
          const errorData = await response.json().catch(() => ({}));
          setPaymentInitError(errorData.detail || 'Failed to initialize payment');
        }
      } catch (err) {
        console.error('Error initializing Stripe:', err);
        setPaymentInitError('Failed to initialize payment. Please try again.');
      } finally {
        setInitializing(false);
      }
    };

    initializeStripe();
  }, [props.enrollmentId, paymentStep]);

  const elementsOptions = {
    locale: 'en' as const,
    appearance: {
      theme: 'stripe' as const
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 10000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          padding: '20px',
          borderTopLeftRadius: '12px',
          borderTopRightRadius: '12px'
        }}>
          <h2 style={{ margin: 0, fontSize: '24px' }}>
            💳 Camp Enrollment Payment
            {stripeMode === 'live' && (
              <span style={{ 
                marginLeft: '10px', 
                fontSize: '12px', 
                backgroundColor: '#dc3545', 
                padding: '4px 8px', 
                borderRadius: '4px' 
              }}>
              </span>
            )}
          </h2>
          <p style={{ margin: '5px 0 0 0', fontSize: '14px', opacity: 0.9 }}>
            Secure payment via Stripe {stripeMode === 'live' ? ' (Live Mode)' : ''}
          </p>
        </div>

        {paymentStep === 'choice' ? (
          <div style={{ padding: '30px' }}>
            <h3 style={{ marginBottom: '20px', fontSize: '22px' }}>Choose Payment Method</h3>
            <p style={{ color: '#555', marginBottom: '16px' }}>
              As a not-for-profit society, we prefer Interac e-transfer because it helps us reduce card processing fees.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
              <button
                className="btn btn-primary"
                onClick={() => setPaymentStep('interac')}
                style={{ width: '100%', maxWidth: '280px', whiteSpace: 'normal', lineHeight: 1.35 }}
              >
                Interac e-Transfer (Recommended)
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setPaymentStep('stripe')}
                style={{ width: '100%', maxWidth: '280px', whiteSpace: 'normal', lineHeight: 1.35 }}
              >
                Pay by Credit / Debit Card (Stripe)
              </button>
            </div>
            <div style={{ marginTop: '20px', textAlign: 'center' }}>
              <button className="btn btn-link" onClick={props.onCancel}>
                Close
              </button>
            </div>
          </div>
        ) : paymentStep === 'interac' ? (
          <div style={{ padding: '30px' }}>
            <h3 style={{ marginBottom: '16px', fontSize: '22px' }}>Interac e-Transfer Instructions</h3>
            <p style={{ marginBottom: '8px' }}><strong>E-Transfer Email:</strong> admin@oarayouthinnovationlab.ca</p>
            <p style={{ marginBottom: '8px' }}><strong>Amount:</strong> ${props.invoiceData.total.toFixed(2)} CAD</p>
            <p style={{ color: '#555', marginBottom: '12px' }}>
              Interac transfers can take several hours to complete. We will keep this camp spot open for up to 24 hours.
            </p>
            <div style={{ marginBottom: '16px', background: '#f8f9fa', padding: '12px', borderRadius: '8px' }}>
              <p style={{ margin: '0 0 6px 0', fontWeight: 600 }}>Add below e transfer notes/message</p>
              <code>
                {[props.campName || 'Camp', props.studentName || 'Student'].filter(Boolean).join(', ')}
              </code>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
              <button className="btn btn-secondary" onClick={() => setPaymentStep('choice')}>
                Back
              </button>
              <button
                className="btn btn-primary"
                onClick={async () => {
                  try {
                    setSendingInteracEmail(true);
                    const token = localStorage.getItem('cognitoToken') || '';
                    const response = await fetch(
                      `${config.BACKEND_URL}/api/camps/enrollments/${props.enrollmentId}/send-interac-instructions`,
                      {
                        method: 'POST',
                        headers: {
                          'Authorization': `Bearer ${token}`,
                          'Content-Type': 'application/json'
                        }
                      }
                    );
                    if (!response.ok) {
                      const err = await response.json().catch(() => ({}));
                      console.error('Failed to send Interac instructions email:', err);
                    }
                  } catch (err) {
                    console.error('Error sending Interac instructions email:', err);
                  } finally {
                    setSendingInteracEmail(false);
                    props.onCancel();
                  }
                }}
                disabled={sendingInteracEmail}
              >
                {sendingInteracEmail ? 'Sending Email...' : 'I Will Pay by Interac'}
              </button>
            </div>
          </div>
        ) : initializing ? (
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <p>Loading payment system...</p>
          </div>
        ) : paymentInitError ? (
          <div style={{ padding: '24px' }}>
            <div style={{ 
              padding: '15px', 
              backgroundColor: '#f8d7da', 
              color: '#721c24', 
              borderRadius: '8px',
              marginBottom: '16px'
            }}>
              ❌ {paymentInitError}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setPaymentStep('choice')}>
                Back
              </button>
            </div>
          </div>
        ) : stripePromise && clientSecret ? (
          <Elements stripe={stripePromise} options={elementsOptions}>
            <PaymentForm {...props} clientSecret={clientSecret} stripeMode={stripeMode} />
          </Elements>
        ) : (
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <p>Unable to initialize payment.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CampPaymentModal;

